<template>
  <nav class="blogpost-social-nav">
    <button
      type="button"
      class="blogpost-social-nav__icon blogpost-social-nav__icon--clickboard"
      @click="handleCopyToClickBoard"
    >
      <span
        v-if="isCopiedToClickboard"
        :class="['blogpost-social-nav__notification', visibleNotificationClass]"
        @animationend="handleLeaveAnimation"
      >
        Copied to clickboard.
      </span>
    </button>
    <a
      rel="noreferrer noopener"
      target="_blank"
      class="blogpost-social-nav__icon blogpost-social-nav__icon--twitter"
      :href="twitterHref"
    />
    <a
      rel="noreferrer noopener"
      target="_blank"
      :href="linkedinHref"
      class="blogpost-social-nav__icon blogpost-social-nav__icon--linkedin"
    />
    <a
      rel="noreferrer noopener"
      target="_blank"
      class="blogpost-social-nav__icon blogpost-social-nav__icon--facebook"
      :href="facebookHref"
    />
  </nav>
</template>

<script lang="ts" setup>
import type { SbMetaStoryblok } from '@/types'

interface Props {
  meta?: SbMetaStoryblok
}

const props = defineProps<Props>()

const isCopiedToClickboard = ref(false)
const fullPath = ref('')
const linkedinHref = computed(
  () => `https://www.linkedin.com/sharing/share-offsite/?url=${fullPath.value}`,
)
const twitterHref = computed(() => {
  const title = props.meta?.twitter_title || ''
  return `https://twitter.com/intent/tweet?text=${title}&url=${fullPath.value}`
})
const facebookHref = computed(
  () => `https://www.facebook.com/sharer/sharer.php?u=${fullPath.value}`,
)
const visibleNotificationClass = computed(
  () =>
    isCopiedToClickboard.value && 'blogpost-social-nav__notification--visible',
)

const handleCopyToClickBoard = () => {
  navigator.clipboard.writeText(fullPath.value)
  isCopiedToClickboard.value = true
}

const handleLeaveAnimation = () => {
  isCopiedToClickboard.value = false
}

onMounted(() => {
  fullPath.value = window.location.href
})
</script>

<style lang="scss" scoped>
.blogpost-social-nav {
  display: flex;
  position: relative;
  gap: var(--space12);
  padding-bottom: var(--space48);
  z-index: 80;

  @media (min-width: $breakpoint-lg) {
    top: calc(var(--header-height) + 48px);
    position: sticky;
    display: grid;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-position: 50%;
    background-repeat: no-repeat;
    cursor: pointer;
    transition: border-color 0.12s ease-in;

    &--clickboard {
      position: relative;
      background-image: url('@/assets/icons/social-media/link.svg');
      // preload SVG to eliminate initial blinking on hover
      // https://stackoverflow.com/questions/13286745/css-avoid-image-hover-first-time-blinking
      content: url('@/assets/icons/social-media/link.svg')
        url('@/assets/icons/social-media/link-active.svg');

      &:hover {
        background-image: url('@/assets/icons/social-media/link-active.svg');
      }
    }

    &--twitter {
      background-image: url('@/assets/icons/social-media/twitter.svg');
      content: url('@/assets/icons/social-media/twitter.svg')
        url('@/assets/icons/social-media/twitter-active.svg');

      &:hover {
        background-image: url('@/assets/icons/social-media/twitter-active.svg');
      }
    }

    &--linkedin {
      background-image: url('@/assets/icons/social-media/linkedin.svg');
      content: url('@/assets/icons/social-media/linkedin.svg')
        url('@/assets/icons/social-media/linkedin-active.svg');

      &:hover {
        background-image: url('@/assets/icons/social-media/linkedin-active.svg');
      }
    }

    &--facebook {
      background-image: url('@/assets/icons/social-media/facebook.svg');
      content: url('@/assets/icons/social-media/facebook.svg')
        url('@/assets/icons/social-media/facebook-active.svg');

      &:hover {
        background-image: url('@/assets/icons/social-media/facebook-active.svg');
      }
    }
  }

  &__notification {
    position: absolute;
    top: 50%;
    left: 100%;
    transform: translate(0, -50%);
    min-width: max-content;
    border-radius: var(--border-radius-large);
    background-color: var(--cobalt-800);
    color: var(--white);
    padding-inline: var(--space12);
    cursor: auto;

    @include paragraph-m;

    &--visible {
      animation: fade-notification 1s forwards;
    }
  }
}

@keyframes fade-notification {
  0% {
    transform: translate(0, -50%);
    opacity: 0;
  }

  20% {
    transform: translate(12px, -50%);
    opacity: 1;
  }

  80% {
    transform: translate(12px, -50%);
    opacity: 1;
  }

  100% {
    transform: translate(0, -50%);
    opacity: 0;
  }
}
</style>
